import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { ApplicationStage, CommonHelper, EntityStatus } from 'common';
import { map, mapTo, retry, switchMap } from 'rxjs/operators';
import { AutosaveFormDetailsParams, BusinessInformationFormData } from '../../form-details.model';
import { SubmissionsFacade } from '../../../../../../domain/+state/submissions.facade';

interface AccountDiffData extends Omit<BusinessInformationFormData, 'requestedAmount'> { }
interface AppDiffData extends Pick<BusinessInformationFormData, 'requestedAmount'> { }

interface BusinessDiffData {
  accountDiff: AccountDiffData;
  appDiff: AppDiffData;
}

@Injectable({
  providedIn: 'root'
})
export class AutosaveBusinessInfoStepService {
  constructor(private readonly submissionsFacade: SubmissionsFacade) { }

  createNewAccountAndApplication(params: AutosaveFormDetailsParams, payload: BusinessInformationFormData): Observable<AutosaveFormDetailsParams> {
    const { accountDiff, appDiff }: BusinessDiffData = this.splitBusinessDiff(payload);

    return this.createNewAccount(params.brokerId, accountDiff).pipe(
      switchMap((accountId: number) => this.createNewApplication(params.brokerId, accountId, appDiff).pipe(
        map((applicationId: number) => ({ brokerId: params.brokerId, accountId, applicationId }))
      )))
  }

  updateAccountAndCreateNewApplication(params: AutosaveFormDetailsParams, payload: BusinessInformationFormData): Observable<AutosaveFormDetailsParams> {
    const { accountDiff, appDiff }: BusinessDiffData = this.splitBusinessDiff(payload);
    const accountObs: Observable<void> = this.isEmpty(accountDiff) ? of(undefined) : this.updateAccount(params.accountId, accountDiff);
    const appObs: Observable<number> = this.createNewApplication(params.brokerId, params.accountId, appDiff);

    return forkJoin([accountObs, appObs]).pipe(
      map(([, applicationId]) => ({
        ...params, applicationId
      }))
    );
  }

  updateExistingAccountAndApplication(params: AutosaveFormDetailsParams, payload: BusinessInformationFormData): Observable<AutosaveFormDetailsParams> {
    const { accountDiff, appDiff }: BusinessDiffData = this.splitBusinessDiff(payload);
    const accountObs: Observable<void> = this.isEmpty(accountDiff) ? of(undefined) : this.updateAccount(params.accountId, accountDiff);
    const appObs: Observable<void> = this.isEmpty(appDiff) ? of(undefined) : this.updateApplication(params.applicationId, appDiff);

    return forkJoin([accountObs, appObs]).pipe(mapTo(params));
  }

  private splitBusinessDiff(payload: BusinessInformationFormData): BusinessDiffData {
    if (!payload) {
      return { accountDiff: {} as AccountDiffData, appDiff: {} as AppDiffData };
    }
    const accountDiff = { ...payload };
    const appDiff: AppDiffData = {};
    if ('requestedAmount' in payload) {
      appDiff.requestedAmount = payload.requestedAmount;
      delete accountDiff.requestedAmount;
    }
    return { accountDiff, appDiff };
  }

  private updateAccount(accountId: number, accountDiff: AccountDiffData): Observable<void> {
    return this.submissionsFacade.updateAccountData(accountId, accountDiff, true).pipe(retry(3), mapTo(undefined));
  }

  private updateApplication(applicationId: number, appDiff: AppDiffData): Observable<void> {
    return this.submissionsFacade.updateApplicationData(applicationId, appDiff, true).pipe(retry(3), mapTo(undefined));
  }

  private createNewAccount(brokerId: number, accountDiff: AccountDiffData): Observable<number> {
    return this.submissionsFacade.createAccountData({
      ...accountDiff,
      brokerId,
      status: EntityStatus.Active,
      active: true,
    }, true).pipe(retry(3), map((data) => <number>data.id));
  }

  private createNewApplication(brokerId: number, accountId: number, appDiff: AppDiffData): Observable<number> {
    return this.submissionsFacade.createApplicationData({
      requestedAmount: appDiff.requestedAmount,
      brokerId,
      accountId,
      active: true,
      stage: ApplicationStage.New,
    }, true).pipe(retry(3), map((data) => <number>data.id));
  }

  private isEmpty(value: unknown): boolean {
    return CommonHelper.deepIsEmpty(value);
  }
}
